import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';
import { useSelector } from 'react-redux';
import { selectUser } from '../../reducers/userSlice';
import { selectRTFO } from '../../reducers/rtfoSlice';
import { useTheme } from '@mui/styles';
import { chartColors } from '../../helpers/chartColors';
import NoChartData from './NoChartData';
import { rtfoConversionFactors } from './rtfoConversionFactors';

export const RTFOMetricsDailyFracSummaryChart = ({ reportDate=new Date(), gridView=true, units }) => {

  const theme = useTheme();
  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;
  
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

  const data = useMemo(() => {
    if (!currentRTFO?.stageData) return [];

    return getChartData();

    function getChartData() {
      try {
        let startTimestamp = new Date(reportDate);
        startTimestamp.setHours(6);
        startTimestamp.setMinutes(0);
        startTimestamp.setSeconds(0);
        startTimestamp.setMilliseconds(0);
        startTimestamp = startTimestamp.getTime();

        const endTimestamp = startTimestamp + 24 * 60 * 60 * 1000;

        const traces = [];
        // find stages that correspond to the day
        const dailyStageNames = currentRTFO?.stages?.filter(s => currentRTFO?.stageData?.[s]?.stageEndTimestamp > startTimestamp && currentRTFO?.stageData?.[s]?.stageEndTimestamp < endTimestamp);

        const X = [];
        const YAvgPressure = [];
        const YAvgRate = [];
        const YTonnagePlaced = [];
        const YMaxProppant = [];

        for (const stageName of dailyStageNames) {
          const stageNumber = stageName?.split('_')?.[2];

          const stage = currentRTFO?.stageData?.[stageName];
          const well = stageName?.split('_')?.[1];

          X.push(`${well} Stg. ${stageNumber}`);
          YAvgPressure.push(stage?.metrics?.averagePumpingPressure * (units === 'field' ? rtfoConversionFactors.pressure : 1));
          YAvgRate.push(stage?.metrics?.averagePumpingRate * (units === 'field' ? rtfoConversionFactors.rate : 1));
          YTonnagePlaced.push(stage?.metrics?.totalProppant * (units === 'field' ? rtfoConversionFactors.totalProppant : 1));
          YMaxProppant.push(stage?.metrics?.maxProppant * (units === 'field' ? rtfoConversionFactors.proppant : 1));
        };

        // pressure trace
        traces.push({
          x: X,
          y: YAvgPressure,
          type: 'scattergl',
          mode: 'lines',
          xaxis: 'x1',
          yaxis: 'y2',
          name: `Avg. Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
          line: {
            color: chartColors[5],
            width: 3
          },
          marker: {
            color: 'transparent'
          },
          hovertemplate: `<b>Stage: %{x}</b><br>Avg. Pressure: %{y:.2f} ${units === 'field' ? 'psi' : 'MPa'}<extra></extra>`
        });
        // rate trace
        traces.push({
          x: X,
          y: YAvgRate,
          type: 'scattergl',
          mode: 'lines',
          xaxis: 'x1',
          yaxis: 'y1',
          name: `Avg. Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
          line: {
            color: chartColors[0],
            width: 3
          },
          marker: {
            color: 'transparent'
          },
          hovertemplate: `<b>Stage: %{x}</b><br>Avg. Rate: %{y:.2f} ${units === 'field' ? 'bbl/min' : 'm3/min'}<extra></extra>`
        });
        // tonnage placed trace
        traces.push({
          x: X,
          y: YTonnagePlaced,
          type: 'scattergl',
          mode: 'markers',
          xaxis: 'x1',
          yaxis: 'y3',
          name: `Proppant Placed (${units === 'field' ? 'lb' : 'ton'})`,
          line: {
            color: chartColors[7],
            width: 3
          },
          marker: {
            color: chartColors[7],
            size: 10
          },
          hovertemplate: `<b>Stage: %{x}</b><br>Proppant Placed: %{y:.2f} ${units === 'field' ? 'lb' : 'ton'}<extra></extra>`
        });
        // max proppant trace
        traces.push({
          x: X,
          y: YMaxProppant,
          type: 'bar',
          xaxis: 'x1',
          yaxis: 'y4',
          name: `Max Proppant Conc. (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
          marker: {
            color: chartColors[1],
          },
          hovertemplate: `<b>Stage: %{x}</b><br>Max Proppant Conc.: %{y:.2f} ${units === 'field' ? 'lb/gal' : 'kg/m3'}<extra></extra>`
        });



        return traces;

  
      } catch (error) {
        console.log(error);
        return [];
      };

    };
  }, [currentRTFO?.stageData, currentRTFO?.stages, reportDate, units]);

  const layout = {
    barmode: 'group',
    yaxis: { // Main y axis
      linecolor: darkMode ? 'lightgrey' : chartColors[0],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : chartColors[0],
      title: {
        text: `Average Slurry Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
        font: {
          size: 12
        },
      },
      anchor: 'free',
      // autorange: true,
      range: units === 'field' ? [0, 150] : [0, 25],
      side: 'left',
      uirevision: 'true',
      rangemode: 'nonnegative',
    },
    yaxis2: { // pressure y axis
      linecolor: chartColors[5],
      showgrid: false,
      color: chartColors[5],
      title: {
        text: `Average Treating Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
        font: {
          size: 12
        },
      },
      anchor: 'x',
      range: units === 'field' ? [0, 15000] : [0, 100],
      side: 'left',
      overlaying: 'y',
      uirevision: 'true',
      zeroline: false
    },
    yaxis3: { // Tonnage placed y axis
      linecolor: chartColors[7],
      showgrid: false,
      color: chartColors[7],
      title: {
        text: `Proppant Placed (${units === 'field' ? 'lb' : 'ton'})`,
        font: {
          size: 12
        },
      },
      anchor: 'x',
      range: units === 'field' ? [0, 500000] : [0, 250],
      side: 'right',
      overlaying: 'y',
      uirevision: 'true',
      zeroline: false
    },
    yaxis4: { // Max Prop y axis
      linecolor: chartColors[1],
      showgrid: false,
      color: chartColors[1],
      title: {
        text: `Max Proppant Conc. (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
        font: {
          size: 12
        },
      },
      anchor: 'free',
      range: units === 'field' ? [0, 10] : [0, 1000],
      position: 1,
      side: 'right',
      overlaying: 'y',
      uirevision: 'true',
      zeroline: false
    },
    xaxis: { // Main x axis
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: 'Stage',
        font: {
          size: 12
        }
      },
      tickfont: {
        size: 10
      },
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      type: 'category',
      domain: [gridView ? 0.1 : 0.05, gridView ? 0.9 : 0.95]
    },
    autosize: true,
    showlegend: true,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: (gridView ? -0.25 : -0.1),
    },
    annotations: [
      {
        text: "Daily Report - Frac Summary",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.1 : 1.06), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name} | Date: ${reportDate?.toLocaleDateString()}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.05 : 1.035), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
    ],
    uirevision: 'true',
    margin: {"t": 40, "l": 60, "r": 50, "b": 0},
    images: [
      {
        x: (gridView ? 0.05 : 0.035),
        y: (gridView ? 1.02 : 1.01),
        sizex: (gridView ? 0.1 : 0.05),
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "y1 domain",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: `${currentRTFO?.name}-Daily Frac Summary-${(new Date(reportDate).toISOString().split('T')[0])}`,
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <div style={{position: 'relative', width: '100%', height: '100%'}}>
      {data?.length > 0 ?
        <Plot
          data={data}
          layout={layout}
          config={config}
          useResizeHandler={true}
          style={{width: '100%', height: '100%'}}
        />
        :
        <div style={{width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <NoChartData />
        </div>
      } 
    </div>
  );

  
};


export const RTFOMetricsDailyFracSummaryChartMemo = React.memo(RTFOMetricsDailyFracSummaryChart);